// src/sections/ProblemSolution.jsx
import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { problemSolution } from "../data/siteContent";
import {
  slideLeft,
  slideRight,
  staggerParent,
  viewportOnce,
  cardReveal,
} from "../utils/motion";

export default function ProblemSolution() {
  const metricsRef = useRef(null);
  const isMetricsInView = useInView(metricsRef, { once: true, amount: 0.3 });

  return (
    <section
      className="section-block section-block--light problem-solution-section"
      id="model"
    >
      <div className="container-shell">
        <motion.div
          className="problem-solution-shell"
          variants={staggerParent}
          initial="hidden"
          whileInView="show"
          viewport={viewportOnce}
        >
          <motion.div className="problem-copy" variants={slideLeft}>
            <div className="section-eyebrow">
              <span className="section-eyebrow__dot" />
              {problemSolution.eyebrow}
            </div>
            <h2>{problemSolution.title}</h2>
            <div className="problem-copy__divider" />
            <p>{problemSolution.intro}</p>
          </motion.div>

          <motion.div className="problem-stage" variants={slideRight}>
            <article className="problem-stage__hero">
              <div className="problem-stage__hero-content">
                <div className="problem-stage__hero-top">
                  <span className="problem-stage__badge">
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <circle cx="6" cy="6" r="4" fill="currentColor" />
                    </svg>
                    {problemSolution.problem.eyebrow}
                  </span>
                  <h3>{problemSolution.problem.title}</h3>
                  <p>{problemSolution.problem.desc}</p>
                </div>
              </div>

              <div className="problem-stage__visual" ref={metricsRef}>
                <div className="problem-stage__glow" />
                <div className="problem-stage__grid" />

                <motion.div
                  className="problem-stage__metric problem-stage__metric--left"
                  initial={{ opacity: 0, x: -30 }}
                  animate={isMetricsInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  <span className="problem-stage__metric-label">
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                      <circle cx="5" cy="5" r="4" fill="#b4e920" />
                    </svg>
                    Critical indicator
                  </span>
                  <strong className="problem-stage__metric-value">PM2.5</strong>
                  <span className="problem-stage__metric-sub">
                    4x WHO limit
                  </span>
                </motion.div>

                <motion.div
                  className="problem-stage__metric problem-stage__metric--right"
                  initial={{ opacity: 0, x: 30 }}
                  animate={isMetricsInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.6, delay: 0.4 }}
                >
                  <span className="problem-stage__metric-label">
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                      <circle cx="5" cy="5" r="4" fill="#418917" />
                    </svg>
                    Average fuel waste
                  </span>
                  <strong className="problem-stage__metric-value">
                    {problemSolution.problem.indicator}
                  </strong>
                </motion.div>
              </div>
            </article>

            <div className="problem-stage__solutions">
              {problemSolution.solutions.map((item, index) => (
                <motion.article
                  key={item.title}
                  className="problem-solution-card"
                  variants={cardReveal}
                  whileHover={{ y: -8 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="problem-solution-card__icon">
                    {index === 0 && (
                      <svg
                        width="28"
                        height="28"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <path
                          d="M3 9L12 3L21 9L12 15L3 9Z"
                          stroke="#b4e920"
                          strokeWidth="1.5"
                          fill="none"
                        />
                        <path
                          d="M5 11V17L12 21L19 17V11"
                          stroke="#b4e920"
                          strokeWidth="1.5"
                          fill="none"
                        />
                      </svg>
                    )}
                    {index === 1 && (
                      <svg
                        width="28"
                        height="28"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <path
                          d="M12 2L2 7L12 12L22 7L12 2Z"
                          stroke="#b4e920"
                          strokeWidth="1.5"
                          fill="none"
                        />
                        <path
                          d="M2 17L12 22L22 17"
                          stroke="#b4e920"
                          strokeWidth="1.5"
                          fill="none"
                        />
                        <path
                          d="M2 12L12 17L22 12"
                          stroke="#b4e920"
                          strokeWidth="1.5"
                          fill="none"
                        />
                      </svg>
                    )}
                    {index === 2 && (
                      <svg
                        width="28"
                        height="28"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <path
                          d="M12 2L15 9H22L16 14L19 21L12 16.5L5 21L8 14L2 9H9L12 2Z"
                          stroke="#b4e920"
                          strokeWidth="1.5"
                          fill="none"
                        />
                      </svg>
                    )}
                  </div>
                  <div className="problem-solution-card__number">
                    0{index + 1}
                  </div>
                  <div className="problem-solution-card__body">
                    <h4>{item.title}</h4>
                    <p>{item.desc}</p>
                  </div>
                  <div className="problem-solution-card__line" />
                  <div className="problem-solution-card__hover-glow" />
                </motion.article>
              ))}
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
